import { Link } from "react-router-dom";
import { Plus, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "@/context/CartContext";
import { imgUrl } from "@/lib/api";

export default function ProductCard({ product }) {
    const { addItem } = useCart();
    const { t } = useTranslation();
    const isPen = product.category === "Pens & Devices";
    const hasCompareAt =
        typeof product.compare_at_price === "number" && product.compare_at_price > product.price;
    const discountPct = hasCompareAt
        ? Math.round((1 - product.price / product.compare_at_price) * 100)
        : 0;
    return (
        <div
            className="group relative border border-neutral-200 hover:border-[#00aeef]/60 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,174,239,0.12)] rounded-2xl overflow-hidden flex flex-col"
            data-testid={`product-card-${product.slug}`}
        >
            <Link to={`/product/${product.slug}`} className="block relative aspect-square overflow-hidden bg-white">
                <img
                    src={imgUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    onError={(e) => {
                        e.currentTarget.style.display = "none";
                        e.currentTarget.parentElement?.classList.add("nx-image-fallback");
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60 pointer-events-none" />
                <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] border border-[#00aeef]/40 px-2 py-1 bg-white/70 rounded-lg">
                        {product.purity}
                    </div>
                    {isPen && (
                        <div
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-white bg-[#0e7490] px-2 py-1 font-bold rounded-lg"
                            data-testid={`pen-badge-${product.slug}`}
                        >
                            <Zap className="w-2.5 h-2.5" /> {t("product.penBadge")}
                        </div>
                    )}
                </div>
                <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
                    {hasCompareAt && discountPct > 0 && (
                        <div
                            className="text-[10px] uppercase tracking-[0.22em] text-white bg-[#e11d48] px-2 py-1 font-bold rounded-lg shadow-sm"
                            data-testid={`sale-badge-${product.slug}`}
                        >
                            −{discountPct}%
                        </div>
                    )}
                    {product.featured && (
                        <div className="text-[10px] uppercase tracking-[0.22em] text-white bg-[#0089c8] px-2 py-1 font-bold rounded-lg">
                            {t("product.labPick")}
                        </div>
                    )}
                </div>
            </Link>
            <div className="p-5 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mb-1.5">
                    {product.category}
                </div>
                <Link to={`/product/${product.slug}`} className="block">
                    <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-black transition-colors">
                        {product.name}
                    </h3>
                </Link>
                <div className="text-xs text-neutral-500 mt-1">{product.short_name}</div>
                {product.description && (
                    <p
                        className="text-xs text-neutral-600 mt-3 leading-relaxed line-clamp-3 flex-1"
                        data-testid={`desc-${product.slug}`}
                    >
                        {product.description}
                    </p>
                )}
                <div className="mt-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">{t("product.from")}</div>
                        <div className="flex items-baseline gap-2">
                            <div
                                className="font-display font-bold text-xl"
                                data-testid={`price-${product.slug}`}
                            >
                                £{product.price.toFixed(2)}
                            </div>
                            {hasCompareAt && (
                                <div
                                    className="text-xs text-neutral-400 line-through"
                                    data-testid={`compare-at-${product.slug}`}
                                >
                                    £{product.compare_at_price.toFixed(2)}
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => addItem(product, 1)}
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold bg-white/5 hover:bg-[#f8f8f8] hover:text-black px-3 py-2 border border-neutral-200 hover:border-[#0089c8] rounded-lg transition-colors"
                        data-testid={`add-to-cart-${product.slug}`}
                    >
                        <Plus className="w-3 h-3" /> {t("product.add")}
                    </button>
                </div>
            </div>
        </div>
    );
}
